// Modal.js
import React from "react";
import "./index.css";
import { MdClear, MdOutlineShoppingCart } from "react-icons/md";
const renderProductDetails = (product) => {
    if (!product) return null;

    const renderTwoColumns = (details) => (
        <div className="grid gap-2 md:gap-4 text-xs grid-cols-2">
            {details.map((detail, index) =>
                detail.value ? (
                    <div key={index} className="flex flex-col">
                        <strong>{detail.label}</strong>
                        <span>{detail.value}</span>
                    </div>
                ) : null
            )}
        </div>
    );

    switch (product.product_level_1) {
        case "BIERES":
            return renderTwoColumns([
                { label: "Famille de bière :", value: product.beer_family },
                { label: "Couleur de bière :", value: product.beer_color },
                { label: "Style de bière :", value: product.beer_style },
                { label: "Région de bière :", value: product.beer_region },
                { label: "Type de mousse :", value: product.beer_head_type },
                { label: "Saveur :", value: product.beer_flavor },
                { label: "Amertume :", value: product.beer_bitterness },
                { label: "Sans gluten :", value: product.gluten_free_beer },
            ]);

        case "VINS":
            return renderTwoColumns([
                { label: "Couleur du vin :", value: product.wine_color },
                { label: "Vignoble :", value: product.wine_vineyard },
                { label: "Dénomination :", value: product.wine_denomination },
                { label: "Appellation :", value: product.wine_appellation },
                { label: "Millésime :", value: product.wine_millesime },
                {
                    label: "Teneur en sucre :",
                    value: product.wine_sugar_content,
                },
                { label: "Cépage :", value: product.wine_grape },
            ]);

        case "SPIRITUEUX":
            return renderTwoColumns([
                {
                    label: "Famille de spiritueux :",
                    value: product.spirit_family,
                },
                { label: "Type de spiritueux :", value: product.spirit_type },
                { label: "Millésime :", value: product.spirit_millesime },
                { label: "Âge :", value: product.spirit_age },
                { label: "Région :", value: product.spirit_region },
            ]);

        case "EAUX":
            return renderTwoColumns([
                { label: "Famille d'eau :", value: product.water_family },
            ]);

        case "SOFT DRINKS":
            return renderTwoColumns([
                { label: "Famille de boissons :", value: product.soft_family },
            ]);

        case "JUS DE FRUITS":
            return renderTwoColumns([
                { label: "Famille de jus :", value: product.juice_family },
            ]);

        case "THES ET INFUSIONS":
            return renderTwoColumns([
                { label: "Type de thé :", value: product.tea_type },
                { label: "Couleur du thé :", value: product.tea_color },
                { label: "Saveur de thé :", value: product.tea_flavor },
                { label: "Label :", value: product.tea_label },
            ]);

        case "CAFES":
            return renderTwoColumns([
                { label: "Type de café :", value: product.coffee_type },
                { label: "Décaféiné :", value: product.decaf_coffee },
                { label: "Type de dosette :", value: product.coffee_pod_type },
                {
                    label: "Région de torréfaction :",
                    value: product.coffee_roasting_region,
                },
                {
                    label: "Type de torréfaction :",
                    value: product.coffee_roast,
                },
                { label: "Variété de café :", value: product.coffee_variety },
                { label: "Ingrédients :", value: product.coffee_ingredients },
                { label: "Intensité :", value: product.coffee_intensity },
                { label: "Label :", value: product.coffee_label },
            ]);

        default:
            return <p className="text-xs">Aucune information disponible.</p>;
    }
};

const ProductModal = ({
    showModal,
    setShowModal,
    product,
    setProduct,
    closeOnClickOutside = false,
}) => {
    if (!showModal) return null;
    const handleClickOutside = () => {
        if (closeOnClickOutside) {
            setShowModal(false);
            setProduct(null);
        }
    };

    const stripHtmlTags = (text) => {
        if (!text) return "";
        return text.replace(/<\/?[^>]+(>|$)/g, ""); // Regex to remove HTML tags
    };

    const warehouseMap = {
        1: "Villenave",
        2: "Marsac",
        3: "Jarnac",
        5: "La Teste",
        2801: "Redon",
        8901: "Rennes",
    };

    // Map product IDs to fiche technique filenames
    const ficheTechniqueMap = {
        9671: "Fiche Technique SAUVIGNON Secret de Terroir.pdf",
        9670: "Fiche Technique CHARDONNAY Secret de Terroir.pdf",
        90137: "HCB BLANC NUITON-BEAUNOY.pdf",
        521930: "HCB ROUGE NUITON -BEAUNOY.pdf",
    };

    const ficheTechniqueBaseUrl =
        "https://myway-email.s3.eu-west-1.amazonaws.com/fiche_technique/";

    const productId = product?.product_id;
    const ficheTechniqueFilename = ficheTechniqueMap[productId];
    const ficheTechniqueUrl = ficheTechniqueFilename
        ? `${ficheTechniqueBaseUrl}${ficheTechniqueFilename}`
        : null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 "
            onClick={handleClickOutside}
        >
            <div
                className="bg-white rounded-lg max-md:min-w-[396px] mx-4 md:max-w-1/2 lg:max-w-[877px] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex flex-col w-full">
                    <div className="flex flex-row justify-between items-center mt-6">
                        <p className="text-base  md:text-[18px] font-bold leading-[100%] ml-6">
                            {product?.product_name}
                        </p>
                        {/* Close Button */}
                        <button
                            className="text-[#282828] hover:text-gray-700 mr-[17px]"
                            onClick={() => setShowModal(false)}
                        >
                            <MdClear className="w-[20.5px] h-[20.5px]" />
                        </button>
                    </div>
                    <div className="h-[1px] w-full bg-[#EDEDED] mt-3"></div>

                    <div className="flex flex-col md:flex-row w-full items-center p-3 md:px-4 md:py-0 md:pb-4">
                        <div className=" md:w-2/5 flex flex-row mt-3 md:mt-0 ">
                            <div className="h-[185px] md:h-[314px] flex items-center ">
                                <img
                                    className=" md:w-full object-scale-down max-md:max-h-[161px]"
                                    src={product?.image}
                                    alt={product?.product_name}
                                />
                            </div>
                            <div className="md:hidden w-2/3 text-left">
                                <p className="font-medium text-sm leading-[100%] text-[#084347]">
                                    <strong>Réf:</strong> {product?.product_id}
                                </p>
                                <p className="flex flex-col text-xs leading-[100%] gap-1">
                                    <strong className="text-base font-semibold">
                                        Description :
                                    </strong>{" "}
                                    {product?.product_desc
                                        ? stripHtmlTags(product.product_desc)
                                        : ""}
                                </p>
                            </div>
                        </div>
                        <div className="w-full md:w-3/5 flex flex-col gap-6 md:gap-4 max-md:px-3 max-md:py-4">
                            <p className="max-md:hidden font-medium text-sm leading-[100%] pt-4 text-left text-[#084347]">
                                <strong>Réf:</strong> {product?.product_id}
                            </p>
                            <p className="max-md:hidden flex flex-col text-xs leading-[100%] gap-1 text-left">
                                <strong className="text-base font-semibold">
                                    Description :
                                </strong>{" "}
                                {product?.product_desc
                                    ? stripHtmlTags(product.product_desc)
                                    : ""}
                            </p>
                            <div className="flex flex-col cursor-pointer text-left">
                                <p className="text-xs leading-[100%] gap-1">
                                    <strong className="text-base font-semibold">
                                        Détails du produit :{" "}
                                    </strong>
                                </p>
                                {renderProductDetails(product)}
                            </div>

                            <div className="flex flex-col text-left">
                                <p className="mb-1 text-base font-semibold leading-[100%]">
                                    <strong>Informations generales : </strong>
                                </p>
                                <div className="grid grid-cols-2 gap-6 md:gap-4 text-xs">
                                    <div className="flex flex-col gap-1">
                                        <p className="grid grid-cols-2 gap-3 text-[#1A1A1A] font-normal text-xs leading-[100%]">
                                            <strong className="font-semibold text-xs leading-[100%]">
                                                Pays
                                            </strong>{" "}
                                            {product?.country
                                                ? product.country
                                                : "-"}
                                        </p>
                                        <p className="grid grid-cols-2 gap-3 text-[#1A1A1A] font-normal text-xs leading-[100%]">
                                            <strong className="font-semibold text-xs leading-[100%]">
                                                Format
                                            </strong>{" "}
                                            {product?.container_format
                                                ? product.container_format
                                                : "-"}
                                        </p>
                                        <p className="grid grid-cols-2 gap-3 text-[#1A1A1A] font-normal text-xs leading-[100%]">
                                            <strong className="font-semibold text-xs leading-[100%]">
                                                Consignation
                                            </strong>{" "}
                                            {product?.deposit
                                                ? product.deposit
                                                : "-"}
                                        </p>
                                        <p className="grid grid-cols-2 gap-3 text-[#1A1A1A] font-normal text-xs leading-[100%]">
                                            <strong className="font-semibold text-xs leading-[100%]">
                                                Contenance
                                            </strong>{" "}
                                            {product?.capacity}{" "}
                                            {product?.capacity_unit
                                                ? product.capacity_unit
                                                : ""}
                                        </p>
                                        <p className="grid grid-cols-2 gap-3 text-[#1A1A1A] font-normal text-xs leading-[100%]">
                                            <strong className="font-semibold text-xs leading-[100%]">
                                                Unité de vente
                                            </strong>{" "}
                                            {product?.sales_unit_count
                                                ? product.sales_unit_count
                                                : "-"}
                                        </p>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <p className="grid grid-cols-2 gap-3 text-[#1A1A1A] font-normal text-xs leading-[100%]">
                                            <strong className="font-semibold text-xs leading-[100%]">
                                                Teneur en alcool
                                            </strong>{" "}
                                            {product?.degree
                                                ? product.degree
                                                : "-"}
                                        </p>
                                        <p className="grid grid-cols-2 gap-3 text-[#1A1A1A] font-normal text-xs leading-[100%]">
                                            <strong className="font-semibold text-xs leading-[100%]">
                                                Producteur
                                            </strong>{" "}
                                            {product?.producer
                                                ? product.producer
                                                : "-"}
                                        </p>
                                        <p className="grid grid-cols-2 gap-3 text-[#1A1A1A] font-normal text-xs leading-[100%]">
                                            <strong className="font-semibold text-xs leading-[100%]">
                                                Marque
                                            </strong>{" "}
                                            {product?.manufacturer_brand
                                                ? product.manufacturer_brand
                                                : "-"}
                                        </p>
                                        <p className="grid grid-cols-2 gap-3 text-[#1A1A1A] font-normal text-xs leading-[100%]">
                                            <strong className="font-semibold text-xs leading-[100%]">
                                                Biologique
                                            </strong>{" "}
                                            {product?.organic
                                                ? product.organic
                                                : "-"}
                                        </p>
                                        <p className="grid grid-cols-2 gap-3 text-[#1A1A1A] font-normal text-xs leading-[100%]">
                                            <strong className="font-semibold text-xs leading-[100%]">
                                                EAN
                                            </strong>{" "}
                                            {product?.ean ? product.ean : "-"}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-3 text-left">
                                <div className="flex flex-col gap-1">
                                    <p className="text-base font-semibold leading-[100%]">
                                        <strong>Stock :</strong>
                                    </p>
                                    <div className="flex flex-col text-xs leading-[100%] gap-1 text-[#1A1A1A]">
                                        {product?.stock &&
                                        product.stock.length > 0 ? (
                                            product.stock.map((item, index) => (
                                                <div key={index}>
                                                    {warehouseMap[
                                                        item.warehouse_id
                                                    ] || item.warehouse_id}
                                                </div>
                                            ))
                                        ) : (
                                            <div>Aucun stock disponible</div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <p className="text-base font-semibold leading-[100%]">
                                        <strong>Quantité :</strong>
                                    </p>
                                    <div className="flex flex-col text-xs leading-[100%] text-[#1A1A1A] gap-1">
                                        {product?.stock.map((item, index) => {
                                            return (
                                                <div key={index}>
                                                    {item.quantity
                                                        ? item.quantity
                                                        : "0"}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <p className="text-base font-semibold leading-[100%]">
                                        <strong>Prix :</strong>
                                    </p>
                                    <div className="text-sm font-bold leading-[100%]">
                                        {product?.prices.price_1
                                            ? product.prices.price_1.toFixed(2)
                                            : "0.00"}{" "}
                                        € (HT)
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-5 max-md:justify-center">
                                {/* Fiche technique button */}
                                {/* Add a condition here if the fiche technique URL is dynamic based on the product */}
                                {ficheTechniqueUrl && (
                                    <button
                                        className="flex flex-row items-center bg-black text-white justify-center rounded-[8px] max-md:w-full md:px-3 py-3 md:py-[9px] text-base  "
                                        onClick={() =>
                                            window.open(
                                                ficheTechniqueUrl,
                                                "_blank"
                                            )
                                        }
                                    >
                                        <span className="text-sm font-bold">
                                            Fiche technique
                                        </span>
                                    </button>
                                )}
                                {/* Acheter Button */}
                                <button
                                    className="flex flex-row items-center bg-[#DBFE52] text-[#3F3E3E] justify-center rounded-[8px] max-md:w-full md:px-3 py-3 md:py-[9px] text-base gap-[6px]"
                                    onClick={() =>
                                        window.open(product?.shop_url, "_blank")
                                    }
                                >
                                    <span className="text-sm font-bold ">
                                        Acheter
                                    </span>
                                    <MdOutlineShoppingCart />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductModal;
